import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Trash2, ShoppingBag } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import BookAutocompleteInline from "@/components/BookAutocompleteInline";

const CONDITIONS = ["new", "like_new", "good", "acceptable"];
const CURRENCIES = ["EUR", "USD", "GBP", "MXN", "ARS", "BRL", "CAD"];

function formatPrice(amount, currency, locale = "en") {
    try {
        return new Intl.NumberFormat(locale, {
            style: "currency",
            currency: (currency || "EUR").toUpperCase(),
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        return `${amount} ${currency}`;
    }
}

export default function Sell() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();

    const [form, setForm] = useState({
        title: "",
        author: "",
        isbn: "",
        cover_url: "",
        condition: "good",
        list_price: "",
        price: "",
        currency: "EUR",
        description: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [myListings, setMyListings] = useState(null);
    const [balance, setBalance] = useState(null);

    useEffect(() => {
        if (authLoading) return;
        if (!user || typeof user !== "object") return;
        (async () => {
            try {
                const [list, bal] = await Promise.all([
                    api.get("/marketplace/my-listings"),
                    api.get("/marketplace/seller/balance"),
                ]);
                setMyListings(list.data);
                setBalance(bal.data);
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail));
            }
        })();
    }, [user, authLoading]);

    const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

    const handlePickBook = async (item) => {
        // Autofill from OpenLibrary suggestion + ask backend for a price
        // suggestion if we end up with a list_price.
        setForm((f) => ({
            ...f,
            title: item.title || f.title,
            author: item.author || f.author,
            isbn: item.isbn || f.isbn,
            cover_url: item.cover ? item.cover.replace("-M.jpg", "-L.jpg") : f.cover_url,
        }));
    };

    const suggested = (() => {
        const lp = parseFloat(form.list_price);
        if (!lp || lp <= 0) return null;
        return formatPrice(Math.round(lp * 0.8 * 100) / 100, form.currency, i18n.language);
    })();

    const handleSubmit = async (e) => {
        e.preventDefault();
        const price = parseFloat(form.price);
        if (!price || price <= 0) {
            toast.error("Price must be > 0");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/marketplace/listings", {
                title: form.title.trim(),
                author: form.author.trim(),
                isbn: form.isbn.trim() || null,
                cover_url: form.cover_url.trim() || null,
                condition: form.condition,
                list_price: form.list_price ? parseFloat(form.list_price) : null,
                price,
                currency: form.currency.toLowerCase(),
                description: form.description.trim() || null,
            });
            toast.success(t("marketplace.sell_success"));
            setMyListings((prev) => [data, ...(prev || [])]);
            setForm({
                title: "", author: "", isbn: "", cover_url: "",
                condition: "good", list_price: "", price: "", currency: "EUR", description: "",
            });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/marketplace/listings/${id}`);
            setMyListings((xs) => xs.filter((x) => x.id !== id));
            toast.success(t("marketplace.removed"));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    if (authLoading) return null;
    if (!user || typeof user !== "object") {
        return (
            <main className="min-h-[calc(100vh-73px)] flex flex-col items-center justify-center px-6 text-center">
                <ShoppingBag size={32} strokeWidth={1} className="text-[var(--accent)] mb-4" />
                <p className="font-display text-2xl text-[var(--ink)] mb-3"
                   style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 32' }}>
                    {t("marketplace.sell_login_required")}
                </p>
                <div className="flex gap-3 mt-4">
                    <Link to="/acceder" className="bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] transition-colors px-6 py-2 text-sm uppercase tracking-widest">
                        {t("nav.login")}
                    </Link>
                    <Link to="/registrarse" className="border border-[var(--ink)] text-[var(--ink)] rounded-full hover:bg-[var(--bg-hover)] transition-colors px-6 py-2 text-sm uppercase tracking-widest">
                        {t("nav.register")}
                    </Link>
                </div>
            </main>
        );
    }

    return (
        <main className="min-h-[calc(100vh-73px)] max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
            <div className="border-b border-[var(--border)] pb-10 mb-12">
                <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)] mb-3">
                    {t("marketplace.sell_eyebrow")}
                </p>
                <h1 className="font-display text-5xl md:text-6xl text-[var(--ink)] tracking-tight"
                    style={{ fontVariationSettings: '"wght" 700, "wdth" 80, "opsz" 144' }}>
                    {t("marketplace.sell_title")}
                </h1>
                <p className="text-[var(--ink-muted)] mt-4 text-lg max-w-2xl">{t("marketplace.sell_lead")}</p>
            </div>

            {balance && (balance.balances.length > 0 || (balance.pending && balance.pending.length > 0)) && (
                <section data-testid="seller-balance" className="border border-[var(--border-strong)] bg-[var(--bg-elevated)] px-6 py-5 mb-10">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--accent)] mb-2">
                        {t("marketplace.balance_title")}
                    </p>
                    <div className="flex flex-wrap items-baseline gap-6">
                        {balance.balances.map((b) => (
                            <div key={b.currency} className="flex items-baseline gap-3">
                                <p className="font-display text-3xl text-[var(--ink)] font-mono-num"
                                   style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 48' }}>
                                    {formatPrice(b.available, b.currency, i18n.language)}
                                </p>
                                <p className="text-xs text-[var(--ink-muted)]">
                                    {t(`marketplace.balance_sales_${b.sales === 1 ? "one" : "other"}`, { count: b.sales })}
                                </p>
                            </div>
                        ))}
                    </div>
                    {balance.pending && balance.pending.length > 0 && (
                        <p className="text-xs text-[var(--ink-dim)] mt-3">
                            {balance.pending.map((p) => `${formatPrice(p.pending, p.currency, i18n.language)} (${p.sales})`).join(" · ")} {t("marketplace.escrow_held").toLowerCase()}
                        </p>
                    )}
                    <p className="text-xs text-[var(--ink-dim)] mt-3">{t("marketplace.balance_lead")}</p>
                </section>
            )}

            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label={t("marketplace.sell_form_title")} required hint={t("marketplace.sell_title_hint")}>
                    <BookAutocompleteInline
                        testId="sell-title"
                        value={form.title}
                        onChange={(v) => update("title", v)}
                        onPick={handlePickBook}
                        placeholder=""
                        inputClassName={inputCls}
                    />
                </Field>
                <Field label={t("marketplace.sell_form_author")} required>
                    <input data-testid="sell-author" required value={form.author} onChange={(e) => update("author", e.target.value)} className={inputCls} />
                </Field>
                <Field label={t("marketplace.sell_form_isbn")}>
                    <input data-testid="sell-isbn" value={form.isbn} onChange={(e) => update("isbn", e.target.value)} className={inputCls} />
                </Field>
                <Field label={t("marketplace.sell_form_cover")}>
                    <input data-testid="sell-cover" type="url" value={form.cover_url} onChange={(e) => update("cover_url", e.target.value)} className={inputCls} />
                </Field>
                <Field label={t("marketplace.sell_form_condition")} required>
                    <select data-testid="sell-condition" value={form.condition} onChange={(e) => update("condition", e.target.value)} className={inputCls}>
                        {CONDITIONS.map((c) => <option key={c} value={c}>{t(`marketplace.condition_${c}`)}</option>)}
                    </select>
                </Field>
                <Field label={t("marketplace.sell_form_currency")}>
                    <select data-testid="sell-currency" value={form.currency} onChange={(e) => update("currency", e.target.value)} className={inputCls}>
                        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </Field>
                <Field label={t("marketplace.sell_form_list_price")} hint={suggested ? t("marketplace.sell_suggested", { price: suggested }) : null}>
                    <input data-testid="sell-list-price" type="number" min="0" step="0.01" value={form.list_price} onChange={(e) => update("list_price", e.target.value)} className={inputCls} />
                </Field>
                <Field label={t("marketplace.sell_form_price")} required>
                    <input data-testid="sell-price" type="number" min="0" step="0.01" required value={form.price} onChange={(e) => update("price", e.target.value)} className={inputCls} />
                </Field>
                <div className="md:col-span-2">
                    <Field label={t("marketplace.sell_form_description")}>
                        <textarea data-testid="sell-description" rows={3} value={form.description} onChange={(e) => update("description", e.target.value)} className={`${inputCls} resize-none`} />
                    </Field>
                </div>
                <div className="md:col-span-2">
                    <button
                        data-testid="sell-submit"
                        type="submit"
                        disabled={submitting}
                        className="bg-[var(--ink)] text-[var(--bg)] rounded-full hover:bg-[var(--accent)] disabled:opacity-50 transition-colors px-8 py-3 text-sm uppercase tracking-widest"
                    >
                        {submitting ? t("marketplace.sell_form_submitting") : t("marketplace.sell_form_submit")}
                    </button>
                </div>
            </form>

            {myListings && myListings.length > 0 && (
                <section className="mt-20">
                    <h2 className="font-display text-3xl text-[var(--ink)] mb-6"
                        style={{ fontVariationSettings: '"wght" 600, "wdth" 90, "opsz" 64' }}>
                        {t("marketplace.my_listings")}
                    </h2>
                    <div className="space-y-3">
                        {myListings.map((l) => (
                            <div key={l.id} className="flex items-center justify-between gap-4 border border-[var(--border)] bg-[var(--bg-elevated)] px-5 py-4">
                                <div className="min-w-0">
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--accent)] mb-0.5">
                                        {t(`marketplace.status_${l.status}`)} · {t(`marketplace.condition_${l.condition}`)}
                                    </p>
                                    <p className="font-display text-base text-[var(--ink)] truncate"
                                       style={{ fontVariationSettings: '"wght" 600, "wdth" 95, "opsz" 18' }}>
                                        {l.title}
                                    </p>
                                    <p className="text-xs text-[var(--ink-muted)]">{l.author}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <p className="font-mono-num text-[var(--ink)]">{formatPrice(l.price, l.currency, i18n.language)}</p>
                                    {l.status === "available" && (
                                        <button onClick={() => handleDelete(l.id)} className="text-[var(--ink-dim)] hover:text-[var(--danger)]" aria-label={t("marketplace.remove")}>
                                            <Trash2 size={16} strokeWidth={1.5} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </main>
    );
}

const inputCls =
    "w-full bg-transparent border-b border-[var(--ink)] py-2 text-base text-[var(--ink)] focus:outline-none focus:border-[var(--accent)] transition-colors";

function Field({ label, required, hint, children }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                {label}{required ? " *" : ""}
            </span>
            {children}
            {hint && <p className="text-xs text-[var(--accent)] mt-1.5">{hint}</p>}
        </label>
    );
}
